import { DepositState, EarnDepositQuote } from '@plug-core/hooks/earn/useEarnDeposit'
import { EarnVault } from '@plug-core/interfaces/earn'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { titleize } from '@plug-core/utils/titleize'
import { Button, Heading, ListGroup } from '@ui'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoRow } from './shared'

interface Props {
  vault: EarnVault
  quote: EarnDepositQuote
  depositState: DepositState
  onConfirm: () => void
  onClose: () => void
}

export const EarnDepositReview: FC<Props> = ({
  vault,
  quote,
  depositState,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation()

  // Stays busy while the sheet slides out after success; the parent leaves and shows the pill.
  const busy = depositState === 'loading' || depositState === 'success'

  const gasCostUsd = Number(quote.estimate.gasCosts?.[0]?.amountUSD ?? 0)
  const depositValueUsd = Number(quote.estimate.toAmountUSD ?? 0)

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: t('earn.deposit.vault'), value: vault.name },
    { label: t('earn.protocol'), value: titleize(vault.protocol.name) },
    {
      label: t('earn.apy'),
      value: formatNumber(vault.analytics.apy.total, '%', { maximumFractionDigits: 2 }),
    },
  ]

  if (depositValueUsd > 0) {
    rows.push({ label: t('earn.deposit.depositValue'), value: formatMoney(depositValueUsd) })
  }

  if (gasCostUsd > 0) {
    rows.push({ label: t('earn.deposit.gasFee'), value: formatMoney(gasCostUsd) })
  }

  return (
    <div className="m-2 flex flex-col gap-4">
      <Heading level={4} align="center">
        {t('earn.deposit.reviewTitle')}
      </Heading>

      <ListGroup>
        {rows.map((row) => (
          <InfoRow key={row.label} label={row.label} value={row.value} />
        ))}
      </ListGroup>

      <div className="flex flex-col gap-4">
        <Button variant="primary" fullWidth loading={busy} disabled={busy} onClick={onConfirm}>
          {t('earn.deposit.depositButton')}
        </Button>

        <Button variant="ghost" fullWidth disabled={busy} onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}
